import { generateUuid } from '@/shared/utils';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ContactOtpTipo,
  PortalAlterarContatoEtapa,
  PortalAlterarContatoState,
  PortalListFilters,
  PortalLoginLookupInput,
  PortalState,
} from '../types/portalTypes';

import {
  fetchPortalFaq,
  portalClearLoginRemember,
  portalConfirmContact,
  portalConfirmContactByIdentifier,
  portalGetDetail,
  portalGetLoginRememberHint,
  portalInitiateKycByIdentifier,
  portalListNotificacoes,
  portalLoginByHash,
  portalLogout,
  portalLookupLogin,
  portalRequestContactOtp,
  portalRequestContactOtpByIdentifier,
  portalRequestFallback,
  portalRequestLoginMagicLink,
  portalRequestMagicLink,
  portalUseLoginRemember,
} from './actions';

const shouldIgnorePortalSessionError = (error: string | null | undefined): boolean =>
  error === 'Sessão expirada';

const generateSessaoId = (): string => {
  if (typeof window !== 'undefined') {
    const stored = sessionStorage.getItem('portal_sessao_id');
    if (stored) return stored;
    const newId = generateUuid();
    sessionStorage.setItem('portal_sessao_id', newId);
    return newId;
  }
  return generateUuid();
};

const defaultFilters: PortalListFilters = {
  page: 1,
  pageSize: 10,
};

const initialAlterarContato: PortalAlterarContatoState = {
  etapa: 'idle',
  tipoContato: null,
  verificationId: null,
  verificationUrl: null,
  novoContato: null,
  otpCooldownUntil: null,
  loading: false,
  error: null,
};

const initialState: PortalState = {
  isAuthenticated: false,
  user: null,
  token: null,
  notificacoes: [],
  pagination: {
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  },
  filters: defaultFilters,
  currentNotificacao: null,
  sessaoId: generateSessaoId(),
  loading: false,
  error: null,
  faqItems: [],
  faqLoading: false,
  faqError: null,
  magicLinkPollToken: null,
  alterarContato: initialAlterarContato,
  loginLookup: null,
  rememberHint: null,
  rememberHintLoading: false,
};

const portalSlice = createSlice({
  name: 'portal',
  initialState,
  reducers: {
    resetPortalState: () => initialState,
    clearPortalError: (state) => {
      state.error = null;
    },
    setPortalAuth: (state, action) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token ?? null;
    },
    setPortalFilters: (state, action: { payload: Partial<PortalListFilters> }) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetPortalFilters: (state) => {
      state.filters = defaultFilters;
    },
    startAlterarContato: (state, action: PayloadAction<{ tipo?: ContactOtpTipo }>) => {
      state.alterarContato = {
        ...initialAlterarContato,
        etapa: action.payload.tipo ? 'aviso-kyc' : 'escolhendo-tipo',
        tipoContato: action.payload.tipo ?? null,
      };
    },
    setAlterarContatoEtapa: (state, action: PayloadAction<PortalAlterarContatoEtapa>) => {
      state.alterarContato.etapa = action.payload;
    },
    setAlterarContatoTipo: (state, action: PayloadAction<ContactOtpTipo>) => {
      state.alterarContato.tipoContato = action.payload;
    },
    setAlterarContatoVerificationId: (state, action: PayloadAction<string>) => {
      state.alterarContato.verificationId = action.payload;
    },
    setAlterarContatoNovoContato: (state, action: PayloadAction<string>) => {
      state.alterarContato.novoContato = action.payload;
    },
    setAlterarContatoCooldown: (state, action: PayloadAction<number>) => {
      const ms = action.payload * 1000;
      state.alterarContato.otpCooldownUntil = new Date(Date.now() + ms).toISOString();
    },
    clearAlterarContatoCooldown: (state) => {
      state.alterarContato.otpCooldownUntil = null;
    },
    resetAlterarContato: (state) => {
      state.alterarContato = initialAlterarContato;
    },
    clearPortalLoginLookup: (state) => {
      state.loginLookup = null;
    },
    setPortalLoginLookup: (state, action: PayloadAction<PortalLoginLookupInput>) => {
      state.loginLookup = {
        identifier: action.payload.identifier,
        nome: action.payload.nome,
        channels: action.payload.channels,
        lastCanal: null,
        redirectTo: action.payload.redirectTo,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(portalLoginByHash.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(portalLoginByHash.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token ?? null;
      })
      .addCase(portalLoginByHash.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro ao fazer login';
      })

      .addCase(portalListNotificacoes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(portalListNotificacoes.fulfilled, (state, action) => {
        state.loading = false;
        state.notificacoes = action.payload.items;
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
          hasNext: action.payload.hasNext,
          hasPrevious: action.payload.hasPrevious,
        };
      })
      .addCase(portalListNotificacoes.rejected, (state, action) => {
        state.loading = false;
        state.error = shouldIgnorePortalSessionError(action.payload)
          ? null
          : (action.payload ?? 'Erro ao listar notificações');
      })

      .addCase(portalGetDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(portalGetDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentNotificacao = action.payload;
      })
      .addCase(portalGetDetail.rejected, (state, action) => {
        state.loading = false;
        // Recarga de fundo (a de depois do envio) mantém o que já foi lido: com `error` setado a
        // view trocaria tudo pelo cartão de "indisponível", e quem acabou de responder concluiria
        // que a resposta não foi. É flag explícita, não "mesmo id": reabrir a notificação ainda em
        // state com a rede caindo tem de mostrar o erro, não o detalhe velho como se fosse atual.
        if (action.meta.arg.silencioso) return;
        state.error = shouldIgnorePortalSessionError(action.payload)
          ? null
          : (action.payload ?? 'Erro ao obter detalhes');
      })

      .addCase(portalLogout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.notificacoes = [];
        state.currentNotificacao = null;
      })
      .addCase(portalLogout.rejected, (state) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.notificacoes = [];
        state.currentNotificacao = null;
      })

      .addCase(fetchPortalFaq.pending, (state) => {
        state.faqLoading = true;
        state.faqError = null;
      })
      .addCase(fetchPortalFaq.fulfilled, (state, action) => {
        state.faqLoading = false;
        state.faqItems = action.payload;
      })
      .addCase(fetchPortalFaq.rejected, (state, action) => {
        state.faqLoading = false;
        state.faqError = action.error.message ?? 'Erro ao carregar FAQ';
      })
      .addCase(portalRequestMagicLink.fulfilled, (state, action) => {
        state.magicLinkPollToken = action.payload.pollToken ?? null;
      })
      .addCase(portalRequestFallback.fulfilled, (state, action) => {
        if (action.payload.pollToken) {
          state.magicLinkPollToken = action.payload.pollToken;
        }
      })

      .addCase(portalRequestContactOtp.pending, (state) => {
        state.alterarContato.loading = true;
        state.alterarContato.error = null;
      })
      .addCase(portalRequestContactOtp.fulfilled, (state) => {
        state.alterarContato.loading = false;
        state.alterarContato.etapa = 'confirmando-otp';
        state.alterarContato.otpCooldownUntil = new Date(Date.now() + 30_000).toISOString();
      })
      .addCase(portalRequestContactOtp.rejected, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.error = action.payload ?? 'Erro ao enviar código';
      })

      .addCase(portalConfirmContact.pending, (state) => {
        state.alterarContato.loading = true;
        state.alterarContato.error = null;
      })
      .addCase(portalConfirmContact.fulfilled, (state) => {
        state.alterarContato.loading = false;
        state.alterarContato.etapa = 'sucesso';
        state.alterarContato.otpCooldownUntil = null;
      })
      .addCase(portalConfirmContact.rejected, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.error = action.payload ?? 'Código inválido ou expirado';
      })

      .addCase(portalRequestContactOtpByIdentifier.pending, (state) => {
        state.alterarContato.loading = true;
        state.alterarContato.error = null;
      })
      .addCase(portalRequestContactOtpByIdentifier.fulfilled, (state) => {
        state.alterarContato.loading = false;
        state.alterarContato.etapa = 'confirmando-otp';
        state.alterarContato.otpCooldownUntil = new Date(Date.now() + 30_000).toISOString();
      })
      .addCase(portalRequestContactOtpByIdentifier.rejected, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.error = action.payload ?? 'Erro ao enviar código';
      })

      .addCase(portalConfirmContactByIdentifier.pending, (state) => {
        state.alterarContato.loading = true;
        state.alterarContato.error = null;
      })
      .addCase(portalConfirmContactByIdentifier.fulfilled, (state) => {
        state.alterarContato.loading = false;
        state.alterarContato.etapa = 'sucesso';
        state.alterarContato.otpCooldownUntil = null;
      })
      .addCase(portalConfirmContactByIdentifier.rejected, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.error = action.payload ?? 'Código inválido ou expirado';
      })

      .addCase(portalRequestLoginMagicLink.fulfilled, (state, action) => {
        const expiresIn = action.payload.expiresIn;
        const previous = state.loginLookup;
        state.loginLookup = {
          identifier: action.meta.arg.identifier,
          redirectTo: action.meta.arg.redirectTo,
          nome: previous?.identifier === action.meta.arg.identifier ? previous.nome : undefined,
          channels: previous?.identifier === action.meta.arg.identifier ? previous.channels : [],
          lastCanal: action.payload.canal ?? null,
          selectedHint: action.payload.selectedHint ?? null,
          expiresAt: expiresIn ? new Date(Date.now() + expiresIn * 1000).toISOString() : null,
        };
      })
      .addCase(portalLookupLogin.fulfilled, (state, action) => {
        state.loginLookup = {
          identifier: action.meta.arg.identifier,
          redirectTo: action.meta.arg.redirectTo,
          nome: action.payload.nome,
          channels: action.payload.channels ?? [],
          lastCanal: null,
        };
      })

      .addCase(portalGetLoginRememberHint.pending, (state) => {
        state.rememberHintLoading = true;
      })
      .addCase(portalGetLoginRememberHint.fulfilled, (state, action) => {
        state.rememberHintLoading = false;
        state.rememberHint = action.payload;
      })
      .addCase(portalGetLoginRememberHint.rejected, (state) => {
        state.rememberHintLoading = false;
        state.rememberHint = null;
      })
      .addCase(portalClearLoginRemember.fulfilled, (state) => {
        state.rememberHint = null;
      })
      .addCase(portalUseLoginRemember.fulfilled, (state, action) => {
        const expiresIn = action.payload.expiresIn;
        const previous = state.loginLookup;
        state.loginLookup = {
          identifier: state.rememberHint?.hint ?? previous?.identifier ?? '',
          nome: previous?.nome,
          channels: action.payload.channels ?? previous?.channels ?? [],
          lastCanal: action.payload.canal ?? null,
          viaRemember: true,
          redirectTo: action.meta.arg.redirectTo,
          selectedHint: action.payload.selectedHint ?? null,
          expiresAt: expiresIn ? new Date(Date.now() + expiresIn * 1000).toISOString() : null,
        };
      })

      .addCase(portalInitiateKycByIdentifier.pending, (state) => {
        state.alterarContato.loading = true;
        state.alterarContato.error = null;
      })
      .addCase(portalInitiateKycByIdentifier.fulfilled, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.verificationId = action.payload.verificationId;
        state.alterarContato.verificationUrl = action.payload.verificationUrl;
        state.alterarContato.etapa = 'verificando-kyc';
      })
      .addCase(portalInitiateKycByIdentifier.rejected, (state, action) => {
        state.alterarContato.loading = false;
        state.alterarContato.error =
          action.payload ?? 'Não foi possível iniciar a verificação. Tente novamente.';
      });
  },
});

export const {
  setPortalAuth,
  setPortalFilters,
  resetPortalFilters,
  startAlterarContato,
  setAlterarContatoEtapa,
  setAlterarContatoTipo,
  setAlterarContatoVerificationId,
  setAlterarContatoNovoContato,
  setAlterarContatoCooldown,
  clearAlterarContatoCooldown,
  resetAlterarContato,
  clearPortalLoginLookup,
  setPortalLoginLookup,
} = portalSlice.actions;

export default portalSlice.reducer;
